import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import styled from 'styled-components';
import TrueImpactLoading from '../TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { ANIMATIONS } from '../../design-system/settings/variables/Animation';

export const DefaultGridNav: React.FC<{ groupName: string }> = ({
  groupName,
}) => {
  return (
    <StyledTableGroup>
      <TrueImpactText variant="heading-subheading" className="nav-group-name">
        {groupName}
      </TrueImpactText>
      <TrueImpactLoading />
    </StyledTableGroup>
  );
};

export const StyledTableGroup = styled.div`
  width: 100%;
  max-width: 224px;
  border-top: 2px solid #fff;
  border-bottom: 2px solid #fff;
  background-color: ${COLORS.greyLightest};
  .nav-group-name {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    margin: 0;
    color: ${COLORS.whiteBase};
    background-color: ${COLORS.grayDark};
    padding: ${SPACERS.small};
  }
  .item {
    cursor: pointer;
    position: relative;
    padding: ${SPACERS.small};
    transition: all 0.15s ${ANIMATIONS.easing};
  }
  .item.active,
  .item:hover {
    :before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: 2px;
      background-color: ${COLORS.ctaPrimary};
    }
  }
  .item.active {
    background-color: ${COLORS.greyLight};
  }
  .item:hover {
    background-color: ${COLORS.ctaPrimaryBackground};
  }
`;

interface GridNavProps<T, U> {
  items: T[];
  extraData: U;
  groupName: string;
  className?: string;
  initialActiveIndex?: number;
  renderItem: (
    item: T,
    index: number,
    isActive: boolean,
    extra: U,
  ) => React.ReactNode;
  getKey: (item: T, index: number, extra: U) => string | number;
  onClick: (item: T, index: number, extra: U) => unknown;
}

export interface GridNavHandle<T> {
  getActiveItem: () => T | undefined;
}

const GridNavImpl = <T, U>(
  {
    groupName,
    items,
    extraData,
    className,
    initialActiveIndex,
    getKey,
    onClick,
    renderItem,
  }: GridNavProps<T, U>,
  ref: React.Ref<GridNavHandle<T>>,
) => {
  const [activeIndex, setActiveIndex] = useState<number>(
    initialActiveIndex ?? 0,
  );

  useEffect(() => {
    const startIndex =
      initialActiveIndex != null &&
      initialActiveIndex >= 0 &&
      initialActiveIndex < items.length
        ? initialActiveIndex
        : 0;
    setActiveIndex(startIndex);
    onClick(items[startIndex], startIndex, extraData);
  }, [groupName, items, setActiveIndex]);

  const handleClick = useCallback(
    (item: T, index: number) => {
      setActiveIndex(index);
      onClick(item, index, extraData);
    },
    [extraData, onClick, setActiveIndex],
  );

  useImperativeHandle(
    ref,
    () => ({
      getActiveItem: () => items[activeIndex],
    }),
    [activeIndex, items, extraData, onClick],
  );

  return (
    <StyledTableGroup className={className}>
      <TrueImpactText variant="heading-subheading" className="nav-group-name">
        {groupName}
      </TrueImpactText>
      {items.map((t, i) => (
        <div
          key={getKey(t, i, extraData)}
          onClick={() => handleClick(t, i)}
          className={`item${i === activeIndex ? ' active' : ''}`}
        >
          {renderItem(t, i, i === activeIndex, extraData)}
        </div>
      ))}
    </StyledTableGroup>
  );
};

const GridNav = React.forwardRef(GridNavImpl) as <T, U>(
  props: GridNavProps<T, U> & { ref?: React.Ref<GridNavHandle<T>> },
) => JSX.Element;

export default React.memo(GridNav) as typeof GridNav;
